import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';

export type CadixDockSide = 'left' | 'right';
export type CadixWorkspaceMode = 'docked' | 'detached';

interface StoredWorkspaceState {
  version: 1;
  open: boolean;
  dockSide: CadixDockSide;
  dockWidth: number;
}

export const CADIX_WORKSPACE_STORAGE_KEY = 'cadix-workspace-v1';
export const CADIX_DOCK_MIN_WIDTH = 22 * 16;
export const CADIX_NARROW_BREAKPOINT = 992;
const DEFAULT_DOCK_WIDTH = 360;
const DOCUMENT_MIN_WIDTH = 24 * 16;

/**
 * Root CADIX workspace state. Conversation state remains in CadixChatService;
 * this service owns only placement, visibility, and responsive layout.
 */
@Injectable({ providedIn: 'root' })
export class CadixWorkspaceStateService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewportWidth = signal(this.readViewportWidth());
  private readonly restored = this.restore();
  private persistenceEnabled = true;

  readonly open = signal(this.restored.open);
  readonly dockSide = signal<CadixDockSide>(this.restored.dockSide);
  readonly dockWidth = signal(this.clampDockWidth(this.restored.dockWidth));
  readonly mode = signal<CadixWorkspaceMode>('docked');

  readonly isNarrow = computed(() => this.viewportWidth() < CADIX_NARROW_BREAKPOINT);
  readonly maximumDockWidth = computed(() => {
    const viewport = this.viewportWidth();
    return Math.max(
      CADIX_DOCK_MIN_WIDTH,
      Math.floor(Math.min(viewport * 0.8, viewport - DOCUMENT_MIN_WIDTH)),
    );
  });
  readonly dockedOpen = computed(() => this.open() && this.mode() === 'docked');
  readonly fullPage = computed(() => this.dockedOpen() && this.isNarrow());

  constructor() {
    const resize = () => {
      this.viewportWidth.set(this.readViewportWidth());
      this.dockWidth.update((width) => this.clampDockWidth(width));
    };
    globalThis.addEventListener?.('resize', resize);
    this.destroyRef.onDestroy(() => globalThis.removeEventListener?.('resize', resize));

    effect(() => {
      const state: StoredWorkspaceState = {
        version: 1,
        open: this.open(),
        dockSide: this.dockSide(),
        dockWidth: this.dockWidth(),
      };
      if (this.persistenceEnabled) {
        this.persist(state);
      }
    });
  }

  openPanel(): void {
    this.persistenceEnabled = true;
    this.mode.set('docked');
    this.open.set(true);
  }

  closePanel(): void {
    this.persistenceEnabled = true;
    this.open.set(false);
    this.mode.set('docked');
  }

  toggle(): void {
    if (this.open()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  setDockSide(side: CadixDockSide): void {
    this.persistenceEnabled = true;
    this.dockSide.set(side);
  }

  setDockWidth(width: number): void {
    this.persistenceEnabled = true;
    this.dockWidth.set(this.clampDockWidth(width));
  }

  detach(): void {
    this.persistenceEnabled = true;
    this.open.set(true);
    this.mode.set('detached');
  }

  redock(): void {
    this.openPanel();
  }

  clearSessionStorage(): void {
    this.persistenceEnabled = false;
    this.open.set(false);
    this.mode.set('docked');
    this.dockSide.set('right');
    this.dockWidth.set(this.clampDockWidth(DEFAULT_DOCK_WIDTH));
    try {
      sessionStorage.removeItem(CADIX_WORKSPACE_STORAGE_KEY);
    } catch {
      // In-memory state is still cleared when storage is inaccessible.
    }
  }

  private clampDockWidth(width: number): number {
    const finiteWidth = Number.isFinite(width) ? Math.round(width) : DEFAULT_DOCK_WIDTH;
    const minimumBoundedWidth = Math.max(finiteWidth, CADIX_DOCK_MIN_WIDTH);
    if (this.viewportWidth() < CADIX_NARROW_BREAKPOINT) {
      return minimumBoundedWidth;
    }
    return Math.min(
      minimumBoundedWidth,
      Math.max(
        CADIX_DOCK_MIN_WIDTH,
        Math.floor(
          Math.min(
            this.viewportWidth() * 0.8,
            this.viewportWidth() - DOCUMENT_MIN_WIDTH,
          ),
        ),
      ),
    );
  }

  private readViewportWidth(): number {
    return typeof globalThis.innerWidth === 'number'
      ? globalThis.innerWidth
      : CADIX_NARROW_BREAKPOINT;
  }

  private restore(): StoredWorkspaceState {
    try {
      const parsed = JSON.parse(
        sessionStorage.getItem(CADIX_WORKSPACE_STORAGE_KEY) ?? 'null',
      ) as Partial<StoredWorkspaceState> | null;
      if (
        parsed?.version === 1 &&
        typeof parsed.open === 'boolean' &&
        (parsed.dockSide === 'left' || parsed.dockSide === 'right') &&
        typeof parsed.dockWidth === 'number'
      ) {
        return {
          version: 1,
          open: parsed.open,
          dockSide: parsed.dockSide,
          dockWidth: parsed.dockWidth,
        };
      }
    } catch {
      // Fall back to defaults for malformed or inaccessible storage.
    }
    return {
      version: 1,
      open: false,
      dockSide: 'right',
      dockWidth: DEFAULT_DOCK_WIDTH,
    };
  }

  private persist(state: StoredWorkspaceState): void {
    try {
      sessionStorage.setItem(CADIX_WORKSPACE_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Workspace state is best-effort session data.
    }
  }
}
